import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import html2pdf from "html2pdf.js";
import { Button } from "@/components/ui/button";
import { DD } from "@/constants/testIds";
import { FileText, RotateCcw, FileDown, ChevronDown, ChevronUp } from "lucide-react";
import { parseReport } from "@/lib/parseReport";
import { linkifyCitations } from "@/lib/linkifyCitations";
import RagDashboard from "@/components/RagDashboard";
import CueDrawer from "@/components/CueDrawer";

// Extracted to module scope so the array reference is stable across renders
// (inline `remarkPlugins={[remarkGfm]}` allocates a new array every render).
const REMARK_PLUGINS = [remarkGfm];

const TOC_ITEMS = [
    { id: "section-1", label: "1. Verdict & summary" },
    { id: "section-2", label: "2. Summary table" },
    { id: "section-3", label: "3. Priority cues" },
    { id: "section-4", label: "4. Annexures" },
];

function slugify(text) {
    return String(text)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .substring(0, 80);
}

const MARKDOWN_COMPONENTS = {
    h2: ({ node: _node, children, ...props }) => {
        // children may be a string, an array of nodes, or a mix. Flatten to text.
        const arr = Array.isArray(children) ? children : [children];
        const text = arr
            .map((c) => (typeof c === "string" ? c : (c?.props?.children ?? "")))
            .map((c) => (typeof c === "string" ? c : ""))
            .join("")
            .trim();
        const id = slugify(text);
        const m = text.match(/^(\d+)\./);
        const sectionId = m ? `section-${m[1]}` : null;
        return (
            <h2 id={id} {...props}>
                {sectionId && <span id={sectionId} className="sr-only" aria-hidden="true" />}
                {children}
            </h2>
        );
    },
    a: ({ node: _node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
    ),
};

export default function ReportViewer({ markdown, meta, onReset }) {
    const parsed = useMemo(() => parseReport(markdown), [markdown]);
    const reportRef = useRef(null);
    const [openCueId, setOpenCueId] = useState(null);
    const [annexOpen, setAnnexOpen] = useState(false);

    // Linkify every markdown blob we render.
    const overviewMd = useMemo(
        () => linkifyCitations(parsed.overview, parsed.annexure),
        [parsed]
    );
    const riskMd = useMemo(
        () => linkifyCitations(parsed.riskAssessmentMd, parsed.annexure),
        [parsed]
    );
    const conclusionMd = useMemo(
        () => linkifyCitations(parsed.conclusionMd, parsed.annexure),
        [parsed]
    );
    const summaryNarrativeMd = useMemo(
        () => linkifyCitations(parsed.summaryNarrativeMd, parsed.annexure),
        [parsed]
    );
    const priorityCuesMd = useMemo(
        () => linkifyCitations(parsed.priorityCuesMd, parsed.annexure),
        [parsed]
    );
    const annexuresMd = useMemo(
        () => linkifyCitations(parsed.annexuresMd, parsed.annexure),
        [parsed]
    );
    const legacyPostMd = useMemo(
        () => linkifyCitations(parsed.legacyPost, parsed.annexure),
        [parsed]
    );

    const cueCardsLinked = useMemo(() => {
        const out = {};
        for (const [k, card] of Object.entries(parsed.cueCards || {})) {
            out[k] = {
                ...card,
                plainEnglish: linkifyCitations(card.plainEnglish || "", parsed.annexure),
                detail: linkifyCitations(card.detail || "", parsed.annexure),
            };
        }
        return out;
    }, [parsed]);

    useEffect(() => {
        if (reportRef.current) reportRef.current.scrollTop = 0;
    }, [markdown]);

    const openCue = cueCardsLinked[openCueId];

    // Section 5 has real content only if there's at least one `#### Cue N —`
    // card in the priority body. Otherwise (e.g. "No cues meet the priority
    // threshold" placeholder), we suppress the whole section from the PDF.
    const hasRealPriorityCues = /^####\s+Cue\s+\d+/m.test(parsed.priorityCuesMd || "");

    const fileBase = (meta?.ngoName || "ngo")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

    const handleDownloadMarkdown = () => {
        const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dd-${fileBase}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadPdf = (variant = "full") => {
        const node = document.getElementById("dd-report-printable");
        if (!node) return;
        node.classList.add("pdf-export-mode");
        if (variant === "brief") node.classList.add("pdf-brief-mode");
        const ngoTitle = meta?.ngoName || "Due Diligence Report";
        const genDate = new Date().toLocaleDateString("en-GB", {
            year: "numeric", month: "long", day: "numeric",
        });
        const suffix = variant === "brief" ? "brief" : "full";

        // Annexure DOM is now always mounted; we only need to give the
        // browser a couple of frames to apply the pdf-export-mode class
        // (which reveals the annex body) and let layout settle.
        const waitForAnnex = () => new Promise((resolve) => {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setTimeout(resolve, 300))
            );
        });

        waitForAnnex().then(() => {
            // Scale must be modest to fit the full annexure inside html2canvas's
            // ~16k-pixel canvas ceiling. A 15k-px source at scale 2.5 would be
            // truncated (~37k px, exceeds the limit) — brief mode can safely
            // use higher scale because its DOM is shorter.
            const scale = variant === "brief" ? 2.2 : 1.5;
            const opts = {
                margin: [20, 14, 18, 14],
                filename: `dd-${fileBase}-${suffix}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale,
                    useCORS: true,
                    letterRendering: true,
                    windowWidth: 1180,
                    scrollX: 0,
                    scrollY: 0,
                },
                jsPDF: {
                    unit: "mm",
                    format: "a4",
                    orientation: "portrait",
                    compress: true,
                },
                pagebreak: {
                    mode: ["css", "legacy"],
                    avoid: [
                        "tr", "thead", "table",
                        "h1", "h2", "h3", "h4",
                        ".pdf-avoid-break",
                        "[data-testid='dd-rag-dashboard']",
                        ".cue-card",
                    ],
                },
            };
            html2pdf()
                .set(opts)
                .from(node)
                .toPdf()
                .get("pdf")
                .then((pdf) => {
                    const totalPages = pdf.internal.getNumberOfPages();
                    const pageW = pdf.internal.pageSize.getWidth();
                    const pageH = pdf.internal.pageSize.getHeight();
                    // Skip header/footer on the very first page if it looks
                    // like a title page; here we still add subtle running
                    // header + page-x-of-y footer to every page.
                    for (let i = 1; i <= totalPages; i++) {
                        pdf.setPage(i);
                        // Page 1 is the cover — it has its own design (magenta
                        // bar, big title, "Generated <date>"). Skip the
                        // running header/footer there to avoid duplicating
                        // the date + adding an out-of-place strip.
                        if (i === 1) continue;
                        // Magenta accent bar under the header
                        pdf.setFillColor(225, 21, 110); // CIFF magenta
                        pdf.rect(0, 0, pageW, 3, "F");
                        // Running header text
                        pdf.setFont("helvetica", "normal");
                        pdf.setFontSize(8.5);
                        pdf.setTextColor(90, 90, 90);
                        const briefTag = variant === "brief" ? " (Executive Brief)" : "";
                        const headerLeft = `Due Diligence Report — ${ngoTitle}${briefTag}`.slice(0, 100);
                        pdf.text(headerLeft, 14, 10);
                        pdf.text(genDate, pageW - 14, 10, { align: "right" });
                        // Thin footer rule (CIFF magenta)
                        pdf.setDrawColor(225, 21, 110);
                        pdf.setLineWidth(0.35);
                        pdf.line(14, pageH - 12, pageW - 14, pageH - 12);
                        // Footer text
                        pdf.setFontSize(8);
                        pdf.setTextColor(120, 120, 120);
                        const footerLeft = variant === "brief"
                            ? "CIFF · Executive brief · AI-assisted"
                            : "CIFF · Open-source screening · AI-assisted";
                        pdf.text(footerLeft, 14, pageH - 7);
                        // Page counter counts from the content pages (excludes
                        // the cover), so page 2 shows as "Page 1 of N-1".
                        const contentPage = i - 1;
                        const contentTotal = totalPages - 1;
                        pdf.text(
                            `Page ${contentPage} of ${contentTotal}`,
                            pageW - 14, pageH - 7,
                            { align: "right" }
                        );
                    }
                    pdf.save(`dd-${fileBase}-${suffix}.pdf`);
                    node.classList.remove("pdf-export-mode");
                    node.classList.remove("pdf-brief-mode");
                })
                .catch(() => {
                    node.classList.remove("pdf-export-mode");
                    node.classList.remove("pdf-brief-mode");
                });
        });
    };

    // Legacy fallback — if we can't detect the new schema, just dump everything.
    const isLegacy = parsed.schemaVersion === 1;

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden" data-testid={DD.reportContainer}>
            {/* HEADER */}
            <div className="px-8 py-5 border-b border-border bg-white flex items-start justify-between gap-6 no-print">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ciff-inkSoft font-mono">
                        Due diligence report
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-ciff-ink mt-1 tracking-tight truncate">
                        {meta?.ngoName}
                    </h1>
                    <div className="text-xs text-ciff-inkSoft mt-1 font-mono flex gap-2 flex-wrap">
                        <span>{meta?.countryName || meta?.country}</span>
                        {meta?.regId && <span>· {meta.regId}</span>}
                        {meta?.website && <span>· {meta.website}</span>}
                        <span className="opacity-40">·</span>
                        <span>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        onClick={handleDownloadMarkdown}
                        data-testid={DD.downloadMarkdownBtn}
                        variant="outline"
                        className="rounded-sm border-border text-ciff-ink hover:bg-ciff-paper2 h-9 text-sm font-medium"
                    >
                        <FileText className="h-4 w-4 mr-1.5" />
                        Markdown
                    </Button>
                    <div className="flex rounded-sm overflow-hidden" data-testid="dd-download-pdf-group">
                        <Button
                            onClick={() => handleDownloadPdf("brief")}
                            data-testid="dd-download-pdf-brief"
                            variant="outline"
                            className="rounded-none rounded-l-sm border-border border-r-0 text-ciff-ink hover:bg-ciff-paper2 h-9 text-sm font-medium"
                            title="Executive brief — Cover + Verdict + Summary table only (~2-3 pages)"
                        >
                            <FileDown className="h-4 w-4 mr-1.5" />
                            Brief PDF
                        </Button>
                        <Button
                            onClick={() => handleDownloadPdf("full")}
                            data-testid={DD.downloadPdfBtn}
                            className="rounded-none rounded-r-sm bg-ciff-magenta hover:bg-ciff-magentaDeep text-white h-9 text-sm font-semibold"
                            title="Full report with priority cues and annexures (~10-15 pages)"
                        >
                            <FileDown className="h-4 w-4 mr-1.5" />
                            Full PDF
                        </Button>
                    </div>
                    <Button
                        onClick={onReset}
                        data-testid={DD.newReportBtn}
                        variant="outline"
                        className="rounded-sm border-border text-ciff-ink hover:bg-ciff-paper2 h-9 text-sm font-medium"
                    >
                        <RotateCcw className="h-4 w-4 mr-1.5" />
                        New
                    </Button>
                </div>
            </div>

            {/* BODY */}
            <div className="flex-1 min-h-0 overflow-hidden flex">
                {/* TOC */}
                <nav
                    data-testid={DD.reportToc}
                    className="w-64 flex-shrink-0 border-r border-border bg-white h-full overflow-y-auto py-6 no-print"
                >
                    <div className="px-5 text-[10px] uppercase tracking-[0.2em] text-ciff-inkSoft font-mono mb-3">
                        Contents
                    </div>
                    <ul className="space-y-px text-[13px] font-body">
                        {TOC_ITEMS.map((item) => (
                            <li key={item.id}>
                                <a
                                    href={`#${item.id}`}
                                    className="block px-5 py-1.5 text-ciff-inkSoft hover:bg-ciff-paper2 hover:text-ciff-ink border-l-2 border-transparent hover:border-ciff-magenta leading-snug transition-colors duration-150"
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* REPORT */}
                <div
                    ref={reportRef}
                    className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-ciff-paper"
                >
                    <div id="dd-report-printable" className="max-w-4xl mx-auto px-10 py-10 bg-white border-x border-border min-h-full">
                        {/* PDF-only cover block */}
                        <div className="pdf-cover pdf-only">
                            <div className="pdf-cover-bar" />
                            <div className="pdf-cover-eyebrow">
                                Due Diligence Report
                            </div>
                            <h1 className="pdf-cover-title">
                                {meta?.ngoName || "Subject Organisation"}
                            </h1>
                            {[meta?.countryName || meta?.country, meta?.regId, meta?.website].filter(Boolean).length > 0 && (
                                <div className="pdf-cover-meta">
                                    {[meta?.countryName || meta?.country, meta?.regId, meta?.website].filter(Boolean).join("  ·  ")}
                                </div>
                            )}
                            <div className="pdf-cover-date">
                                Generated {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}
                            </div>
                            <div className="pdf-cover-footer">
                                Open-source screening · AI-assisted · for internal use
                            </div>
                        </div>

                        {/* Header (title + subtitle) */}
                        {parsed.header && (
                            <div className="report-prose">
                                <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                    {parsed.header}
                                </ReactMarkdown>
                            </div>
                        )}

                        {isLegacy ? (
                            <>
                                {/* Legacy rendering (old 15-section reports) */}
                                <div className="report-prose">
                                    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                        {legacyPostMd}
                                    </ReactMarkdown>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Section 1 — Verdict & summary */}
                                <div className="report-prose pdf-avoid-break">
                                    <h2 id="section-1">1. Verdict &amp; Summary</h2>
                                </div>
                                {overviewMd && (
                                    <div className="report-prose">
                                        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                            {overviewMd}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {/* Section 2 — Risk Assessment */}
                                {riskMd && (
                                    <div className="report-prose">
                                        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                            {riskMd}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {/* Section 3 — Conclusion */}
                                {conclusionMd && (
                                    <div className="report-prose pdf-avoid-break">
                                        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                            {conclusionMd}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {/* Section 4 — Summary Table (interactive RAG dashboard) */}
                                <div className="report-prose">
                                    <h2 id="section-2">2. Summary Table</h2>
                                    <p className="text-[13px] text-ciff-inkSoft leading-relaxed no-print-pdf">
                                        Click any row to open its detail card. The OSF/Soros row is
                                        row 0 (a red row triggers the overall veto).
                                    </p>
                                </div>
                                <div className="my-4 pdf-avoid-break">
                                    <RagDashboard
                                        rows={parsed.ragRows}
                                        annexure={parsed.annexure}
                                        onSelect={(id) => setOpenCueId(id)}
                                    />
                                </div>
                                {summaryNarrativeMd && (
                                    <div className="report-prose">
                                        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                            {summaryNarrativeMd}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {/* Section 3 — Priority cues (always render heading so
                                    reader can see that triage was performed;
                                    in PDF mode we hide the whole block when
                                    there are no red/amber cues to avoid an
                                    awkward empty heading before the annexure). */}
                                <div
                                    id="section-3-block"
                                    className={hasRealPriorityCues ? "" : "no-priority-cues"}
                                >
                                    <div className="report-prose">
                                        <h2 id="section-3">3. Priority Cues</h2>
                                    </div>
                                    {hasRealPriorityCues ? (
                                        <div className="report-prose priority-cues-block">
                                            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                                                {priorityCuesMd}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <div className="report-prose">
                                            <p className="text-[13px] text-ciff-inkSoft italic mt-2">
                                                <em>No red or amber cues — see annexure for full cue detail.</em>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Section 4 — Annexures (collapsible). Always
                                    rendered in the DOM so the PDF export
                                    doesn't race a React re-render; hidden via
                                    CSS when collapsed. */}
                                <div className="mt-8 border-t border-ciff-rule pt-6" id="section-4">
                                    <button
                                        onClick={() => setAnnexOpen((v) => !v)}
                                        data-testid="dd-toggle-details"
                                        className="w-full flex items-center justify-between px-4 py-3 border border-ciff-rule bg-ciff-paper2 hover:bg-ciff-paper hover:border-ciff-magenta transition-colors duration-150 group no-print"
                                    >
                                        <div className="text-left">
                                            <div className="font-heading text-base font-semibold text-ciff-ink">
                                                {annexOpen ? "Hide" : "Show"} annexures
                                            </div>
                                            <div className="text-[11px] text-ciff-inkSoft font-mono mt-0.5">
                                                Remaining cue cards · detailed narrative · full source list · disclaimer
                                            </div>
                                        </div>
                                        {annexOpen
                                            ? <ChevronUp className="h-5 w-5 text-ciff-magenta" strokeWidth={2} />
                                            : <ChevronDown className="h-5 w-5 text-ciff-magenta group-hover:translate-y-0.5 transition-transform duration-150" strokeWidth={2} />}
                                    </button>

                                    {annexuresMd && (
                                        <div
                                            data-testid={DD.reportMarkdown}
                                            className={`report-prose mt-8 annex-body ${annexOpen ? "annex-open" : "annex-closed"}`}
                                        >
                                            <h2 id="section-4-heading" className="!mt-0">4. Annexures</h2>
                                            <ReactMarkdown
                                                remarkPlugins={REMARK_PLUGINS}
                                                components={MARKDOWN_COMPONENTS}
                                            >
                                                {annexuresMd}
                                            </ReactMarkdown>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <CueDrawer
                card={openCue}
                annexure={parsed.annexure}
                open={Boolean(openCue)}
                onClose={() => setOpenCueId(null)}
            />
        </div>
    );
}
